/**
 * Imperative confirm() replacement that renders a styled modal.
 *
 * Usage:
 *   const ok = await confirm({ title, message, confirmText: 'Delete', tone: 'danger' });
 *   if (!ok) return;
 */
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import Modal from './Modal';
import Button from './Button';

let renderer = null;
function ensureRenderer() {
  if (renderer) return renderer;
  if (typeof document === 'undefined') return null;
  const host = document.createElement('div');
  host.id = 'os-confirm-root';
  document.body.appendChild(host);
  renderer = ReactDOM.createRoot(host);
  return renderer;
}

function Dialog({ opts, resolve }) {
  const [open, setOpen] = useState(true);
  const close = (val) => {
    setOpen(false);
    setTimeout(() => resolve(val), 120);
  };
  useEffect(() => () => resolve(false), []); // safety
  return (
    <Modal
      open={open}
      onClose={() => close(false)}
      title={opts.title ?? 'Are you sure?'}
      subtitle={opts.subtitle}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)} testID="confirm-cancel">
            {opts.cancelText ?? 'Cancel'}
          </Button>
          <Button
            variant={opts.tone === 'danger' ? 'danger' : 'primary'}
            onClick={() => close(true)}
            testID="confirm-ok"
          >
            {opts.confirmText ?? 'Confirm'}
          </Button>
        </>
      }
      testID="confirm-dialog"
    >
      <p className="text-sm text-white/70">{opts.message}</p>
    </Modal>
  );
}

export function confirm(opts) {
  return new Promise((resolve) => {
    const r = ensureRenderer();
    if (!r) { resolve(false); return; }
    const onResolve = (v) => {
      try { r.render(null); } catch { /* noop */ }
      resolve(v);
    };
    r.render(<Dialog opts={opts} resolve={onResolve} />);
  });
}
